import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    id: 'web',
    icon: 'ri-layout-4-line',
    title: 'WEBサイト制作',
    subtitle: 'Web Design & Development',
    description:
      'コーポレートサイト・ランディングページ・ECサイトまで、デザインから実装まで一貫対応。集客・成約につながる戦略的なWEBサイトを、丁寧なヒアリングをもとに制作します。',
    tags: ['コーポレートサイト', 'LP制作', 'ECサイト', 'WordPress'],
    bg: '#F8F4EE',
    accent: '#C9A878',
    pricing: [
      { name: 'ランディングページ', price: '¥50,000〜', note: '1ページ完結型' },
      { name: 'コーポレートサイト', price: '¥150,000〜', note: '5〜10ページ標準構成' },
      { name: 'ECサイト', price: '¥250,000〜', note: 'カート・決済機能込み' },
      { name: '月額保守・管理', price: '¥5,000〜/月', note: 'セキュリティ・更新対応' },
    ],
    flow: [
      { step: '01', label: 'ヒアリング', desc: '事業内容・目標・競合・ターゲット層を丁寧にヒアリング' },
      { step: '02', label: '設計・デザイン', desc: 'ワイヤーフレーム作成後、ブランドに合ったデザインを提案' },
      { step: '03', label: '実装・開発', desc: 'コーディングおよびCMS構築。スマホ対応も標準対応' },
      { step: '04', label: '公開・引き渡し', desc: '最終確認後に公開。運用マニュアルもご提供します' },
    ],
  },
  {
    id: 'sns',
    icon: 'ri-instagram-line',
    title: 'SNS運用代行',
    subtitle: 'Social Media Management',
    description:
      'Instagram・X（旧Twitter）・Facebookの投稿企画・制作・分析をまるごとお任せ。ブランドの世界観を守りながら、フォロワー増加・エンゲージメント向上を実現します。',
    tags: ['Instagram', 'X / Twitter', 'Facebook', 'コンテンツ制作'],
    bg: '#F0F6F2',
    accent: '#2E5540',
    pricing: [
      { name: 'ライトプラン', price: '¥19,800〜/月', note: '月8投稿・レポート付き' },
      { name: 'スタンダードプラン', price: '¥29,800〜/月', note: '月12投稿・戦略MTG込み' },
      { name: 'プレミアムプラン', price: '¥49,800〜/月', note: '月16投稿＋広告運用込み' },
      { name: '写真・動画撮影', price: '¥30,000〜/回', note: '撮影・編集・素材提供' },
    ],
    flow: [
      { step: '01', label: 'ヒアリング・戦略設計', desc: 'ターゲット・競合・投稿方針・トーン＆マナーを策定' },
      { step: '02', label: 'コンテンツ制作', desc: '投稿画像・キャプション・ハッシュタグを月次で一括制作' },
      { step: '03', label: '投稿・運用', desc: '最適な時間帯に投稿。コメント・DM対応も対応可能' },
      { step: '04', label: '分析・改善', desc: '月次レポートで数値を振り返り、翌月の施策に反映' },
    ],
  },
  {
    id: 'marketing',
    icon: 'ri-bar-chart-grouped-line',
    title: 'デジタルマーケティング',
    subtitle: 'Digital Marketing',
    description:
      'SEO対策・Web広告（Google/Meta）・MEO対策など、デジタル集客の施策を総合的にサポート。データ分析に基づいたPDCAで、費用対効果の高いマーケティングを実行します。',
    tags: ['SEO対策', 'MEO対策', 'Google広告', 'Meta広告'],
    bg: '#F8F4EE',
    accent: '#C9A878',
    pricing: [
      { name: 'MEO対策', price: '¥20,000〜/月', note: 'Googleマップ・口コミ最適化' },
      { name: 'SEO対策', price: '¥20,000〜/月', note: 'キーワード選定・記事制作含む' },
      { name: 'Web広告運用', price: '¥50,000〜/月', note: '広告費別途・レポート付き' },
      { name: 'ホットペッパー最適化', price: '¥15,000〜/月', note: 'プロフィール・写真・予約改善' },
    ],
    flow: [
      { step: '01', label: '現状分析', desc: '既存サイト・SNS・口コミの現状を数値で把握・課題抽出' },
      { step: '02', label: '施策立案', desc: '課題と目標に応じた最適なデジタル集客施策を設計' },
      { step: '03', label: '実施・運用', desc: '施策を実装し、広告・SEO・MEOの運用を開始' },
      { step: '04', label: 'レポート・改善', desc: '月次レポートで成果を共有し、PDCAを高速で回す' },
    ],
  },
];

export default function Services() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [openFlow, setOpenFlow] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.srv-fade').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
                (el as HTMLElement).classList.remove('opacity-0', 'translate-y-8');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-28 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="srv-fade opacity-0 translate-y-8 transition-all duration-700 mb-16 md:mb-20">
          <p className="text-[#2E5540] text-xs tracking-[0.3em] mb-4 uppercase">/ Services</p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1C2B22] tracking-tight leading-tight mb-5">
            提供サービス
          </h2>
          <p className="text-[#666] text-lg leading-relaxed max-w-xl">
            WEBサイト制作からSNS運用・デジタルマーケティングまで、<br className="hidden md:block" />
            ビジネス成長を加速させる支援を行っています。
          </p>
        </div>

        {/* Service Blocks */}
        <div className="space-y-10">
          {services.map((s) => (
            <div
              key={s.id}
              id={s.id}
              className="srv-fade opacity-0 translate-y-8 transition-all duration-700 rounded-2xl overflow-hidden"
              style={{ backgroundColor: s.bg }}
            >
              {/* Top Section */}
              <div className="p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                {/* Left: Overview */}
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#1C2B22] shrink-0">
                      <i className={`${s.icon} text-2xl text-white`} />
                    </div>
                    <div>
                      <p className="text-[#2E5540] text-xs tracking-[0.2em] uppercase">{s.subtitle}</p>
                      <h3 className="text-2xl font-bold text-[#1C2B22]">{s.title}</h3>
                    </div>
                  </div>
                  <p className="text-[#555] text-sm leading-relaxed mb-6">{s.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {s.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1 rounded-full bg-white/80 text-[#2E5540] border border-[#2E5540]/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Right: Pricing */}
                <div>
                  <p className="text-xs tracking-[0.25em] text-[#888] uppercase mb-4">/ 料金目安</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {s.pricing.map((p) => (
                      <div key={p.name} className="bg-white rounded-xl p-4">
                        <p className="text-[11px] text-[#888] mb-1">{p.name}</p>
                        <p className="text-lg font-bold text-[#1C2B22] leading-tight">{p.price}</p>
                        <p className="text-[11px] text-[#999] mt-1">{p.note}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#aaa] mt-3 leading-relaxed">
                    ※ 上記は目安価格です。規模・要件により異なります。詳細はお気軽にご相談ください。
                  </p>
                </div>
              </div>

              {/* Flow Toggle */}
              <div className="border-t border-black/5">
                <button
                  type="button"
                  onClick={() => setOpenFlow(openFlow === s.id ? null : s.id)}
                  className="w-full flex items-center justify-between px-8 md:px-12 py-5 cursor-pointer hover:bg-black/[0.02] transition-colors"
                >
                  <span className="text-sm font-medium text-[#1C2B22] tracking-wide flex items-center gap-2">
                    <i className="ri-route-line text-[#2E5540]" />
                    支援フロー（ご依頼の流れ）を見る
                  </span>
                  <i className={`ri-arrow-down-s-line text-[#888] text-lg transition-transform duration-300 ${openFlow === s.id ? 'rotate-180' : ''}`} />
                </button>

                {/* Flow Steps */}
                <div
                  className={`overflow-hidden transition-all duration-500 ${openFlow === s.id ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="px-8 md:px-12 pb-8 md:pb-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {s.flow.map((f, idx) => (
                        <div key={f.step} className="relative">
                          {/* Connector line */}
                          {idx < s.flow.length - 1 && (
                            <div className="hidden lg:block absolute top-5 left-full w-full h-px bg-[#2E5540]/20 z-0" style={{ width: 'calc(100% - 2rem)' }} />
                          )}
                          <div className="bg-white rounded-xl p-5 relative z-10">
                            <div
                              className="w-8 h-8 flex items-center justify-center rounded-full text-white text-xs font-bold mb-3"
                              style={{ backgroundColor: '#1C2B22' }}
                            >
                              {f.step}
                            </div>
                            <p className="text-sm font-bold text-[#1C2B22] mb-2">{f.label}</p>
                            <p className="text-xs text-[#666] leading-relaxed">{f.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="srv-fade opacity-0 translate-y-8 transition-all duration-700 mt-14 bg-[#1C2B22] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-[#C9A878] text-xs tracking-[0.3em] mb-2 uppercase">/ Free Consultation</p>
            <h4 className="text-xl md:text-2xl font-bold text-white mb-2">
              まずは無料でご相談ください
            </h4>
            <p className="text-white/50 text-sm leading-relaxed">
              予算・スケジュール・ご要望など、お気軽にお問い合わせください。
            </p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 bg-[#C9A878] text-[#1C2B22] px-8 py-4 rounded-full text-sm tracking-widest font-bold hover:bg-[#B8916A] transition-colors whitespace-nowrap"
          >
            無料相談を申し込む
          </Link>
        </div>
      </div>
    </section>
  );
}
